import React, { useState } from 'react';
import { Modal } from '@mantine/core';
import { formatWoltPreorderTime, resolveWoltPhase, type OrderView } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { aggregatorChannelLabel } from '../aggregator-order.utils';
import { useWoltOrderActions } from './useWoltOrderActions';
import { WoltRejectModal } from './WoltRejectModal';
import { visibleWoltActions, woltStatusLabel } from './wolt-order.utils';
import classes from './wolt-order-panel.module.scss';

export type WoltOrderPanelProps = {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  order: OrderView;
  // Accept succeeded: the parent prints this device's bon (+ receipt when the order closed).
  onAccepted?: (order: OrderView) => void;
};

// Channel card for a Wolt order: header, meta line, customer block, total, lifecycle buttons.
export const WoltOrderPanel: React.FC<WoltOrderPanelProps> = ({ tenantId, employeeSession, isManager, order, onAccepted }) => {
  const { inFlight, apply } = useWoltOrderActions({ tenantId, employeeSession, orderId: order.id });
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isPreorderConfirmOpen, setIsPreorderConfirmOpen] = useState(false);

  if (!order.woltOrderId) {
    return null;
  }

  const channelLabel = aggregatorChannelLabel(order.orderCode);
  const actions = visibleWoltActions(order.woltStatus, isManager);
  const preorderTime = formatWoltPreorderTime(order.woltPreorderTime);
  const isBusy = inFlight !== null;
  const isRejectedButOpen = resolveWoltPhase(order.woltStatus) === 'rejected' && order.status === 'open';

  const runAccept = async (): Promise<boolean> => {
    const updated = await apply('accept');
    if (!updated) {
      return false;
    }
    onAccepted?.(updated);
    return true;
  };

  const handleAccept = () => {
    if (preorderTime) {
      setIsPreorderConfirmOpen(true);
      return;
    }
    void runAccept();
  };

  return (
    <div className={classes.panel} data-testid="wolt-order-panel">
      <div className={classes.header}>
        <span className={classes.brand}>{channelLabel}</span>
        <span className={classes.number} data-testid="wolt-order-number">#{order.woltOrderNumber ?? order.woltOrderId}</span>
        <span className={classes.badge} data-testid="wolt-order-status">{woltStatusLabel(order.woltStatus)}</span>
      </div>
      {preorderTime && (
        <div className={classes.meta}>
          <span className={classes.preorder} data-testid="wolt-order-preorder">הזמנה מראש: {preorderTime}</span>
        </div>
      )}
      {(order.guestName || order.woltNote) && (
        <div className={classes.customer} data-testid="wolt-order-customer">
          {order.guestName && (
            <p className={classes.guest} data-testid="wolt-order-guest">{order.guestName}</p>
          )}
          {order.woltNote && (
            <p className={classes.note} data-testid="wolt-order-note">{order.woltNote}</p>
          )}
        </div>
      )}
      <div className={classes.total} data-testid="wolt-order-total">
        <span>סה"כ</span>
        <span>{formatMoney(Number(order.totalAmount ?? 0))}</span>
      </div>
      {isRejectedButOpen && (
        <p className={classes.hint} data-testid="wolt-order-rejected-hint">נדחתה ב{channelLabel} — יש לבטל את הפריטים ידנית</p>
      )}
      {actions.length > 0 && (
        <div className={classes.actions}>
          {actions.includes('accept') && (
            <Button variant="save" fullWidth onClick={handleAccept} disabled={isBusy} loading={inFlight === 'accept'} dataTestId="wolt-order-accept">
              קבל
            </Button>
          )}
          {actions.includes('reject') && (
            <Button variant="cancel" fullWidth onClick={() => setIsRejectOpen(true)} disabled={isBusy} dataTestId="wolt-order-reject">
              דחה
            </Button>
          )}
          {actions.includes('ready') && (
            <Button variant="confirm" fullWidth onClick={() => void apply('ready')} disabled={isBusy} loading={inFlight === 'ready'} dataTestId="wolt-order-ready">
              מוכן
            </Button>
          )}
          {actions.includes('delivered') && (
            <Button variant="confirm" fullWidth onClick={() => void apply('delivered')} disabled={isBusy} loading={inFlight === 'delivered'} dataTestId="wolt-order-delivered">
              נמסר
            </Button>
          )}
        </div>
      )}
      <WoltRejectModal
        opened={isRejectOpen}
        isSubmitting={inFlight === 'reject'}
        onClose={() => setIsRejectOpen(false)}
        onConfirm={async (reason) => {
          if (await apply('reject', reason)) {
            setIsRejectOpen(false);
          }
        }}
      />
      <Modal
        opened={isPreorderConfirmOpen}
        onClose={() => setIsPreorderConfirmOpen(false)}
        title={`אישור הזמנה מראש — ${channelLabel}`}
        centered
        radius="md"
        zIndex={302}
        closeButtonProps={{ 'data-testid': 'wolt-preorder-close' } as Record<string, string>}
      >
        <div className={classes.modalBody} data-testid="wolt-preorder-modal">
          <p className={classes.modalText}>ההזמנה מתוזמנת ל-{preorderTime}. {channelLabel} תעביר אותה להכנה במועד.</p>
          <div className={classes.modalActions}>
            <Button variant="cancel" fullWidth onClick={() => setIsPreorderConfirmOpen(false)} disabled={isBusy} dataTestId="wolt-preorder-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              loading={inFlight === 'accept'}
              onClick={async () => {
                if (await runAccept()) {
                  setIsPreorderConfirmOpen(false);
                }
              }}
              dataTestId="wolt-preorder-confirm"
            >
              אשר הזמנה
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
